/**
 * B-3 — draft-week journey client (mirror of engineDraft.ts).
 *
 * Talks to the proposal-first server lane (flag `draft_week_journey_v1`):
 *   - requestDraftProposal → POST /api/plans/draft-week/propose  (author + HOLD;
 *     NOTHING is written to training_plans)
 *   - fetchHeldProposals   → GET  /api/plans/draft-week/pending  (resume read)
 *   - confirmDraftWeeks    → POST /api/plans/draft-week/confirm   (APPROVE — the
 *     ONLY training_plans write; non-destructive + versioned)
 *
 * Plus the pure client helpers the takeover route needs: weeks-param parsing,
 * the origin label, and the deterministic Airo slot-fill (NO LLM renders any part
 * of the review — the card is a slot-filled template, SP-3 precedent).
 */

import type { PlanV1, PlanV1Day } from './parsePlanV1Json';
import type { ParsedPlanTable } from './planTableParser';
import { planV1ToPlanTable } from './dbToPlanTable';
import { resolveMode } from './trainingModes';
import { fromMiles, toMiles } from './actualForPlanDay';
import { parseWeekParam } from './parseWeekParam';

export type AuthFetch = (url: string, init?: { method?: string; body?: string }) => Promise<Response>;

/** One held (proposed) week returned by propose / pending. */
export interface HeldWeek {
  weekNumber: number;
  mode: string | null;
  plan: PlanV1;
  evaluation?: unknown;
  clinical?: unknown;
  adaptation?: unknown;
}

/** One cross-week ramp disclosure (surfaced verbatim via warningLine). */
export interface DraftWarning {
  weekNumber: number;
  severity: 'warn' | 'block';
  [key: string]: unknown;
}

export interface DraftProposeResult {
  state?: 'proposed' | 'needs_review' | 'data_missing' | 'disabled' | string;
  weeks?: HeldWeek[];
  warnings?: DraftWarning[];
  /** Data gate fired (missing training data) → run the SP-3 collection beat. */
  missing?: string[];
  /** A week refused mid-batch → the safe prefix is held; `failedWeek` is which. */
  partial?: boolean;
  failedWeek?: number;
  failedState?: string;
  blockedSpike?: boolean;
  validationErrors?: Array<{ path: string; message: string }>;
  [key: string]: unknown;
}

export interface DraftConfirmResult {
  state?: 'landed' | 'partial' | 'needs_review' | 'save_failed' | 'no_proposal' | 'disabled' | string;
  weeks?: Array<{ weekNumber: number; state: string }>;
  partial?: boolean;
  failedWeek?: number;
  failedState?: string;
  [key: string]: unknown;
}

/** POST the propose request. Null on a network/parse error (→ draft_failed). */
export async function requestDraftProposal(
  fetchAuthed: AuthFetch,
  seasonId: string,
  weeks: number[],
): Promise<DraftProposeResult | null> {
  try {
    const res = await fetchAuthed('/api/plans/draft-week/propose', {
      method: 'POST',
      body: JSON.stringify({ seasonId, weeks }),
    });
    return (await res.json()) as DraftProposeResult;
  } catch {
    return null;
  }
}

/** GET the held proposals for these weeks (resume). Null on error. */
export async function fetchHeldProposals(
  fetchAuthed: AuthFetch,
  seasonId: string,
  weeks: number[],
): Promise<DraftProposeResult | null> {
  try {
    const qs = `seasonId=${encodeURIComponent(seasonId)}&weeks=${encodeURIComponent(weeks.join(','))}`;
    const res = await fetchAuthed(`/api/plans/draft-week/pending?${qs}`);
    return (await res.json()) as DraftProposeResult;
  } catch {
    return null;
  }
}

/** POST the confirm (approve) request. Null on a network/parse error. */
export async function confirmDraftWeeks(
  fetchAuthed: AuthFetch,
  seasonId: string,
  weeks: number[],
): Promise<DraftConfirmResult | null> {
  try {
    const res = await fetchAuthed('/api/plans/draft-week/confirm', {
      method: 'POST',
      body: JSON.stringify({ seasonId, weeks }),
    });
    return (await res.json()) as DraftConfirmResult;
  } catch {
    return null;
  }
}

// ── Pure helpers ─────────────────────────────────────────────────────────────

/** The server's batch maximum (`MAX_BATCH` in `api/index.js`): a draft never
 *  authors more than four weeks, so a longer list has nowhere to go. */
const MAX_WEEKS = 4;

/**
 * Parse the `?weeks=` param: "2" → [2], "2-5" → [2,3,4,5]. Ascending, de-duped,
 * and never longer than the server's four.
 *
 * ⚖️ Security F3 (2026-09-08) — the range expanded with no bound, so
 * `/v2/plan/draft?weeks=1-99999999` froze the tab of whoever opened the link,
 * and that link is shareable. The expansion now takes at most `MAX_WEEKS` steps
 * from `N` (counted, never compared, so a huge `N` cannot stall the loop at the
 * float-precision edge) and the whole list is capped at the same maximum.
 */
export function parseWeeksParam(raw: string | null): number[] {
  if (!raw) return [];
  const out = new Set<number>();
  const push = (n: unknown) => { const i = Number(n); if (Number.isInteger(i) && i >= 1) out.add(i); };
  const m = /^\s*(\d+)\s*-\s*(\d+)\s*$/.exec(raw);
  if (m) {
    const from = Number(m[1]);
    const to = Number(m[2]);
    for (let step = 0; step < MAX_WEEKS; step++) {
      const week = from + step;
      if (week > to) break;
      push(week);
    }
  }
  else raw.split(',').forEach(push);
  return [...out].sort((a, b) => a - b).slice(0, MAX_WEEKS);
}

/**
 * 2.0 Train (spec-2.0-build-wave2.md 7.11, TDR-3): the two origins the v2 Draft card (`BatchDraftCard
 * variant="v2"`) sends, `thisweek` (the Train tab, `/plan` with `train_2_0_v1` on) and `season` (the season page one
 * level down, `/plan/season`). Nothing with the flag off sends either; the route reads them only with the flag on.
 */
export const ORIGIN_THIS_WEEK = 'thisweek';
export const ORIGIN_SEASON = 'season';

/** Origin label for the takeover's back affordance (‹ Today / Season plan / Week N / This week). */
export function originLabel(from: string | null): string {
  if (from === 'plan' || from === ORIGIN_SEASON) return 'Season plan';
  if (from === ORIGIN_THIS_WEEK) return 'This week';
  if (from && from.startsWith('week-')) {
    const n = from.slice('week-'.length);
    return /^\d+$/.test(n) ? `Week ${n}` : 'Plan';
  }
  return 'Today';
}

/** Web-absolute return href for an origin (matches the /v2 router basename hrefs). */
export function originHref(from: string | null): string {
  if (from === 'plan' || from === ORIGIN_THIS_WEEK) return '/v2/plan';
  if (from === ORIGIN_SEASON) return '/v2/plan/season';
  if (from && from.startsWith('week-')) {
    const n = from.slice('week-'.length);
    if (/^\d+$/.test(n)) return `/v2/plan/week/${n}`;
  }
  return '/v2/today';
}

/** In-app (basename-relative) return path for react-router navigate(). */
export function originPath(from: string | null): string {
  if (from === 'plan' || from === ORIGIN_THIS_WEEK) return '/plan';
  if (from === ORIGIN_SEASON) return '/plan/season';
  if (from && from.startsWith('week-')) {
    const n = from.slice('week-'.length);
    if (/^\d+$/.test(n)) return `/plan/week/${n}`;
  }
  return '/today';
}

/**
 * FIX-TRAIN (night critic 2026-10-18, TDR-3 "same place"): the `from=` value for the Train page a 2.0 Draft card is
 * mounted on, so the review's back link and "Not now" return to THAT page: the season page (`season`), a week page
 * (`week-N`), else the Train tab (`thisweek`). The router matches paths case-insensitively and with a trailing
 * slash, so this reads the pathname the way `App.tsx`'s title does; a week segment the router would not read as a
 * week (`parseWeekParam`, the one rule) is the Train tab. `originPath` is the other half: for every value returned
 * here it gives back the page that was read (pinned in `draftWeek.origin.test.ts`).
 */
export function originForTrainPath(pathname: string): string {
  const path = pathname.replace(/\/+$/, '').toLowerCase();
  if (path === originPath(ORIGIN_SEASON)) return ORIGIN_SEASON;
  const week = /^\/plan\/week\/([^/]+)$/.exec(path);
  if (week) {
    let raw: string | null;
    try {
      raw = decodeURIComponent(week[1]);
    } catch {
      raw = null;
    }
    const n = parseWeekParam(raw);
    if (n !== null) return `week-${n}`;
  }
  return ORIGIN_THIS_WEEK;
}

const WEEKDAY_NAMES =['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAY_FROM_DOW = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** wai-plan-v1 → the PlanTable shape (reuses planV1ToPlanTable; expanders ride along). */
export function proposalToTable(plan: PlanV1 | null | undefined): ParsedPlanTable | null {
  return planV1ToPlanTable(plan ?? null);
}

/** Count of running days (non-rest) in a held week. */
export function runCount(plan: PlanV1 | null | undefined): number {
  if (!plan || !Array.isArray(plan.days)) return 0;
  return plan.days.filter((d) => d && !d.is_rest_day).length;
}

/**
 * A distance token in MILES: a number immediately followed by mi / mile / miles.
 * The trailing `\b` is load-bearing — it rejects "140 min" (minutes), whose "mi"
 * prefix would otherwise be mis-read as miles. Pace tokens ("8:30/mi") never match
 * because the "/" breaks the digit→unit adjacency.
 */
const MILES_TOKEN_RX = /(\d+(?:\.\d+)?)\s*(?:mi|mile|miles)\b/i;

/** Format a summed mileage: whole numbers stay whole, else one decimal (no float noise). */
function formatMiles(n: number): string {
  const r = Math.round(n * 10) / 10;
  return String(r);
}

/** Miles carried by one day: the first miles token in its details, else its main set. */
function dayMiles(d: PlanV1Day | null | undefined): number {
  if (!d || d.is_rest_day) return 0;
  const details = typeof d.details === 'string' ? d.details : '';
  let m = details.match(MILES_TOKEN_RX);
  if (!m) {
    const main = d.expanded && typeof d.expanded.main_set === 'string' ? d.expanded.main_set : '';
    m = main.match(MILES_TOKEN_RX);
  }
  return m ? Number(m[1]) : 0;
}

/**
 * The weekly mileage label (bare number, e.g. "13"). MILES-ONLY — never minutes.
 *
 * `weekly_total` is authoritative ONLY when it is expressed in miles ("13 mi",
 * "~28 mi"); some plans express it in MINUTES ("140 min"), and pouring that number
 * into a miles-labeled slot was the founder-reported units bug. So: trust a
 * miles-valued `weekly_total`; otherwise SUM the per-day run distances (the same
 * distances the week table shows); if neither yields miles, return '' so the caller
 * OMITS the stat rather than mislabel minutes as miles.
 */
export function milesLabel(plan: PlanV1 | null | undefined): string {
  if (!plan) return '';
  // 1) weekly_total, but only when it is explicitly in miles (rejects "140 min").
  const wt = typeof plan.weekly_total === 'string' ? plan.weekly_total : '';
  const wtMiles = wt.match(MILES_TOKEN_RX);
  if (wtMiles) return formatMiles(Number(wtMiles[1]));
  // 2) Sum per-day distances parsed from the plan itself.
  let sum = 0;
  for (const d of plan.days || []) sum += dayMiles(d);
  return sum > 0 ? formatMiles(sum) : '';
}

// ── B331 — a week's volume in the athlete's own unit ─────────────────────────
// `milesLabel` above stays miles-only and unchanged (ProposalFrame,
// useHeldWeekProposal and chat.tsx read it). The draft review reads these
// instead: distance in the athlete's unit, minutes when the plan is time-based,
// and null when the plan carries no number at all.

/** The athlete's distance unit (`resolveDistanceUnit` in lib/vdot). */
export type DraftUnit = 'mi' | 'km';

/** One week's volume: a distance in the athlete's unit, or minutes. */
export interface WeekVolume {
  value: number;
  unit: DraftUnit | 'min';
}

/** A distance token in miles OR kilometres ("4.7 mi", "6.3 km"). The trailing `\b`
 * rejects "min"; a pace ("5:20/km") never matches because the "/" breaks the
 * digit-to-unit adjacency. */
const DISTANCE_TOKEN_RX = /(\d+(?:\.\d+)?)\s*(mi|mile|miles|km|kilometer|kilometers|kilometre|kilometres)\b/i;

/** A duration token ("245 min", "40 minutes", "20-30 min": the first value). */
const MINUTES_TOKEN_RX = /(\d+(?:\.\d+)?)(?:\s*(?:-|\u2013|to)\s*\d+(?:\.\d+)?)?\s*min(?:ute)?s?\b/i;

function roundOne(n: number): number {
  return Math.round(n * 10) / 10;
}

/** The number a distance token carries, expressed in `unit`. */
function tokenIn(m: RegExpMatchArray | null, unit: DraftUnit): number {
  if (!m) return 0;
  const n = Number(m[1]);
  if (!Number.isFinite(n)) return 0;
  const isKm = m[2].toLowerCase().startsWith('k');
  if (isKm === (unit === 'km')) return n;
  return isKm ? toMiles(n, 'km') : fromMiles(n, 'km');
}

/** The first match of `rx` in a day's details, else in its main set. */
function dayMatch(d: PlanV1Day | null | undefined, rx: RegExp): RegExpMatchArray | null {
  if (!d || d.is_rest_day) return null;
  const details = typeof d.details === 'string' ? d.details : '';
  const m = details.match(rx);
  if (m) return m;
  const main = d.expanded && typeof d.expanded.main_set === 'string' ? d.expanded.main_set : '';
  return main.match(rx);
}

/** The week's distance in `unit`: a distance `weekly_total` is authoritative,
 * else the per-day distances summed. 0 when the plan states none. A mile
 * athlete's draft keeps `milesLabel`'s number exactly. */
function weekDistance(plan: PlanV1 | null | undefined, unit: DraftUnit): number {
  if (!plan) return 0;
  if (unit === 'mi') {
    const mi = Number(milesLabel(plan));
    if (mi > 0) return mi;
  }
  const wt = typeof plan.weekly_total === 'string' ? plan.weekly_total : '';
  const stated = tokenIn(wt.match(DISTANCE_TOKEN_RX), unit);
  if (stated > 0) return roundOne(stated);
  let sum = 0;
  for (const d of Array.isArray(plan.days) ? plan.days : []) sum += tokenIn(dayMatch(d, DISTANCE_TOKEN_RX), unit);
  return sum > 0 ? roundOne(sum) : 0;
}

/** The week's planned minutes: a minutes `weekly_total` is authoritative (the
 * engine states one), else the per-day durations summed. 0 when none. */
function weekMinutes(plan: PlanV1 | null | undefined): number {
  if (!plan) return 0;
  const wt = typeof plan.weekly_total === 'string' ? plan.weekly_total : '';
  const stated = wt.match(MINUTES_TOKEN_RX);
  if (stated && Number(stated[1]) > 0) return roundOne(Number(stated[1]));
  let sum = 0;
  for (const d of Array.isArray(plan.days) ? plan.days : []) {
    const m = dayMatch(d, MINUTES_TOKEN_RX);
    if (m) sum += Number(m[1]) || 0;
  }
  return sum > 0 ? roundOne(sum) : 0;
}

/**
 * A held week's volume for the draft review, in the athlete's unit. Distance
 * first (km or mi, converted to `unit`), minutes when the plan is time-based,
 * null when the plan states no number. Never a made-up 0.
 */
export function weekVolume(plan: PlanV1 | null | undefined, unit: DraftUnit): WeekVolume | null {
  const dist = weekDistance(plan, unit);
  if (dist > 0) return { value: dist, unit };
  const mins = weekMinutes(plan);
  return mins > 0 ? { value: mins, unit: 'min' } : null;
}

// ── B338 — the draft flow's chat copy in the athlete's own unit ──────────────
// `airoSingleBody`, `landedChip` and the chat tweak opening used `milesLabel`
// (miles only) and the word "miles", so a km athlete read miles in the sentence
// beside a review that states km. The unit parameter is optional and defaults to
// miles, so every existing caller and its output are unchanged.

/**
 * The week's bare distance number in `unit` ("13", "21"), or '' when the plan
 * states none (never minutes). Miles is `milesLabel` exactly; km is the same
 * reader `weekVolume` gives the review, so the sentence and the review agree.
 */
export function distanceLabel(plan: PlanV1 | null | undefined, unit: DraftUnit = 'mi'): string {
  if (unit !== 'km') return milesLabel(plan);
  const vol = weekVolume(plan, 'km');
  return vol && vol.unit === 'km' ? String(vol.value) : '';
}

/** "13 easy miles" / "21 easy km"; the bare "easy miles" / "easy km" when no number. */
export function easyDistanceClause(plan: PlanV1 | null | undefined, unit: DraftUnit = 'mi'): string {
  const n = distanceLabel(plan, unit);
  const word = unit === 'km' ? 'km' : 'miles';
  return n ? `${n} easy ${word}` : `easy ${word}`;
}

/**
 * The batch review's ramp chart series: ONE unit for the whole chart (bars in
 * mixed units would not compare), one value per held week, null for a week with
 * no number in that unit so the chart can say so instead of drawing 0. Distance
 * when every week has one, else minutes when every week has those, else
 * whichever unit at least one week has; `unit` is null only when no week has a
 * number.
 */
export function rampSeries(
  plans: Array<PlanV1 | null | undefined>,
  unit: DraftUnit,
): { unit: DraftUnit | 'min' | null; values: Array<number | null> } {
  const dist = plans.map((p) => weekDistance(p, unit));
  const mins = plans.map((p) => weekMinutes(p));
  const all = (xs: number[]) => xs.length > 0 && xs.every((n) => n > 0);
  const any = (xs: number[]) => xs.some((n) => n > 0);
  const pick = all(dist) ? 'dist' : all(mins) ? 'min' : any(dist) ? 'dist' : any(mins) ? 'min' : null;
  if (pick === null) return { unit: null, values: plans.map(() => null) };
  const src = pick === 'dist' ? dist : mins;
  return { unit: pick === 'dist' ? unit : 'min', values: src.map((n) => (n > 0 ? n : null)) };
}

/** The "feature" clause — the weekday that carries strides/quality (e.g. "strides
 * on Wednesday"), or '' when none stands out. Deterministic scan, no LLM. */
export function featureClause(plan: PlanV1 | null | undefined): string {
  if (!plan || !Array.isArray(plan.days)) return '';
  for (const d of plan.days) {
    if (!d || d.is_rest_day) continue;
    const hay = `${d.workout_type || ''} ${d.details || ''} ${d.expanded?.main_set || ''}`.toLowerCase();
    const weekday = d.day_label ? WEEKDAY_FROM_DOW[Math.max(0, Math.min(6, d.day_of_week ?? 0))] : null;
    if (!weekday) continue;
    if (hay.includes('stride')) return `strides on ${weekday}`;
    if (hay.includes('interval') || hay.includes('tempo') || hay.includes('threshold')) {
      const kind = hay.includes('tempo') ? 'tempo' : hay.includes('threshold') ? 'threshold' : 'intervals';
      return `${kind} on ${weekday}`;
    }
  }
  return '';
}

/**
 * The deterministic Airo card body for the SINGLE-week review (§1c). Slot-filled
 * from the held plan — the surrounding phrasing is verbatim. `midweekEntry` flips
 * to the current-week-after-Monday variant.
 */
export function airoSingleBody(opts: {
  plan: PlanV1 | null;
  midweekEntry: boolean;
  now?: Date;
  /** B338 — the athlete's unit for the distance clause. Default miles. */
  unit?: DraftUnit;
}): string {
  if (opts.midweekEntry) {
    const weekday = WEEKDAY_NAMES[(opts.now ?? new Date()).getDay()];
    return `Your week’s already in motion. It’s ${weekday}, so I planned today through Sunday and kept it easy. Full weeks start Monday. Anything look off? Tell me and I’ll reshape it.`;
  }
  const runs = runCount(opts.plan);
  const feature = featureClause(opts.plan);
  const runsClause = runs > 0 ? `${runs} runs` : 'your runs';
  const milesClause = easyDistanceClause(opts.plan, opts.unit);
  const featureBit = feature ? `, ${feature}` : '';
  return `Your first full week: ${runsClause}, ${milesClause}${featureBit}. A gentle step up from last week, nothing you can’t chat through. Anything look off? Tell me and I’ll reshape it.`;
}

// ── Number-words + week-list joins (batch copy slots, 2–10) ──────────────────
const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];

/** Lowercase English number-word for 0–10; the numeral string above 10. */
export function numberWord(n: number): string {
  return Number.isInteger(n) && n >= 0 && n <= 10 ? NUMBER_WORDS[n] : String(n);
}

function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** Readable numeral list: [2]→"2", [2,3]→"2 and 3", [2,3,4]→"2, 3 and 4". */
function joinWeekNums(nums: number[]): string {
  if (nums.length === 0) return '';
  if (nums.length === 1) return `${nums[0]}`;
  if (nums.length === 2) return `${nums[0]} and ${nums[1]}`;
  return `${nums.slice(0, -1).join(', ')} and ${nums[nums.length - 1]}`;
}

/** Does a week carry a hard quality session (interval / tempo / threshold)? Strides
 * are gentle and do NOT count as quality (they ride the "with strides" clause). */
function weekHasQuality(plan: PlanV1 | null | undefined): boolean {
  if (!plan || !Array.isArray(plan.days)) return false;
  return plan.days.some((d) => {
    if (!d || d.is_rest_day) return false;
    const hay = `${d.workout_type || ''} ${d.details || ''} ${d.expanded?.main_set || ''}`.toLowerCase();
    return hay.includes('interval') || hay.includes('tempo') || hay.includes('threshold');
  });
}

/** Does a week include strides? */
function weekHasStrides(plan: PlanV1 | null | undefined): boolean {
  if (!plan || !Array.isArray(plan.days)) return false;
  return plan.days.some((d) => {
    if (!d || d.is_rest_day) return false;
    const hay = `${d.workout_type || ''} ${d.details || ''} ${d.expanded?.main_set || ''}`.toLowerCase();
    return hay.includes('stride');
  });
}

/**
 * The deterministic Airo card body for the BATCH review (§1d). Every claim is
 * slot-filled from the held plans — NO hardcoded count, block, or "everything easy":
 *   - the count word is the actual batch size ("These three finish…");
 *   - the block name comes from the batch's macro mode ("your Build block");
 *   - the runs cadence is a number-word ("four runs a week");
 *   - the character clause is truthful — "everything easy with strides" only when
 *     no week carries quality work; if any week does, the over-claim is dropped and
 *     the gentle generic ("each step a gentle one") carries it instead;
 *   - `flaggedWeek != null` → the flagged clause; else the gentle-one clause.
 */
export function airoBatchBody(opts: { weeks: HeldWeek[]; flaggedWeek: number | null }): string {
  const count = opts.weeks.length;
  const block = resolveMode(opts.weeks[0]?.mode ?? null).label;
  const runsPerWeek = count > 0 ? runCount(opts.weeks[0].plan) : 0;
  const runsClause = runsPerWeek > 0 ? `${numberWord(runsPerWeek)} runs a week` : 'a few runs a week';

  const anyQuality = opts.weeks.some((w) => weekHasQuality(w.plan));
  const anyStrides = opts.weeks.some((w) => weekHasStrides(w.plan));
  // Truthful character clause. Any quality → don't claim "everything easy"; lean on
  // the gentle generic tail instead. No new sentence shapes beyond README grammar.
  const character = anyQuality ? '' : anyStrides ? 'everything easy with strides' : 'everything easy';

  let head = `These ${numberWord(count)} finish your ${block} block: ${runsClause}`;
  if (character) head += `, ${character}`;

  if (opts.flaggedWeek != null) {
    return `${head}. Week ${opts.flaggedWeek} steps up the most; I've flagged it below. Anything look off? Tell me and I'll reshape any of them.`;
  }
  // No-warning variant: the gentle clause continues the sentence (comma, lowercase),
  // never a broken standalone "Each step…" — matches the README's verbatim wording.
  return `${head}, each step a gentle one. Anything look off? Tell me and I'll reshape any of them.`;
}

/** Clean-batch sub (§1d): "Three weeks, one look. Add them together when they read right." */
export function batchLookSub(count: number): string {
  return `${capitalize(numberWord(count))} weeks, one look. Add them together when they read right.`;
}

/** Partial-batch sub (§1d): names the ready weeks before the refused one, grammatically. */
export function partialReadySub(readyCount: number, failedWeek: number): string {
  const priors = readyCount === 1
    ? "the one before it doesn't"
    : `the ${numberWord(readyCount)} before it don't`;
  return `Week ${failedWeek} needs another look. ${capitalize(priors)} have to wait.`;
}

/**
 * Partial failed-week card body (§1d). Enumerates EVERY ready week (never drops the
 * middle) and names the waiting week when exactly one, truthful plural otherwise.
 */
export function partialRefusalBody(opts: {
  failedWeek: number;
  readyWeeks: number[];
  waitingWeeks: number[];
}): string {
  const ready = [...opts.readyWeeks].sort((a, b) => a - b);
  const waiting = [...opts.waitingWeeks].sort((a, b) => a - b);
  const readyPhrase = ready.length === 1
    ? `Week ${ready[0]} is ready`
    : `Weeks ${joinWeekNums(ready)} are ready`;
  let waitClause = '';
  if (waiting.length === 1) waitClause = `, and week ${waiting[0]} waits for it`;
  else if (waiting.length >= 2) waitClause = `, and weeks ${joinWeekNums(waiting)} wait for it`;
  return `The engine couldn't build week ${opts.failedWeek} inside your limits. ${readyPhrase} now. Reshape week ${opts.failedWeek} with Airo${waitClause}.`;
}

/** The athlete-language cross-week ramp line (verbatim from BatchDraftCard.warningLine). */
export function warningLine(w: { weekNumber: number }): string {
  return `Week ${w.weekNumber} builds up faster than usual from the week before. Ease into its harder days.`;
}

/** The landed ✓-chip copy for the origin surface (§1g). */
export function landedChip(opts: {
  from: string | null;
  weeks: number[];
  firstPlan: PlanV1 | null;
  /** B338 — the athlete's unit for the distance clause. Default miles. */
  unit?: DraftUnit;
}): string {
  const nums = [...weeks(opts.weeks)];
  if ((opts.from === 'plan' || opts.from === ORIGIN_THIS_WEEK || opts.from === ORIGIN_SEASON) && nums.length > 1) {
    return `Weeks ${nums[0]} to ${nums[nums.length - 1]} are in.`;
  }
  const wk = nums[0];
  if (opts.from === 'today' && opts.firstPlan) {
    const runs = runCount(opts.firstPlan);
    const distance = distanceLabel(opts.firstPlan, opts.unit);
    if (runs > 0 && distance) {
      return `Week ${wk} is in: ${runs} runs, ${distance} ${opts.unit === 'km' ? 'km' : 'miles'}.`;
    }
  }
  return `Week ${wk} is in.`;
}

function weeks(ns: number[]): number[] {
  return [...new Set(ns.filter((n) => Number.isInteger(n)))].sort((a, b) => a - b);
}

/** Mode label for the kicker ("BASE"). */
export function modeUpper(mode: string | null): string {
  return mode ? resolveMode(mode).label.toUpperCase() : '';
}
